import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import type { DerivedPhotoEntry } from "./derive-precon-sections";

/** What the lightbox shows: the tapped photo + its document label ("(3a)" / "(U1)"). */
export interface LightboxPhoto {
  photo: DerivedPhotoEntry;
  label: string;
}

/**
 * Thumbnail-as-trigger for the lightbox — every photo thumbnail in the
 * section sheets, "Not located on this plan" groups and General Observations
 * opens its full-size view through this. The accessible name carries the
 * photo's document label so "View photo (3)" / "View photo (U1)" reads the
 * same as the printed reference.
 */
export function PreconPhotoThumbButton({
  photo,
  label,
  onOpen,
}: {
  photo: DerivedPhotoEntry;
  label: string;
  onOpen: (photo: DerivedPhotoEntry, label: string) => void;
}) {
  return (
    <button
      type="button"
      aria-label={`View photo ${label}`}
      onClick={() => onOpen(photo, label)}
      className="shrink-0 overflow-hidden rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <img
        src={photo.thumbnail ?? undefined}
        alt=""
        className="h-16 w-24 object-cover"
      />
    </button>
  );
}

/**
 * Lightweight full-size photo viewer for the builder (US-005): captions on a
 * legal record shouldn't be written against a thumbnail. Shows the largest
 * source the hand-off projection carries — the full-size `image` when
 * present, else the `thumbnail` (legacy projections predate the field).
 * Dismiss returns to editing; the builder's state is untouched.
 *
 * The projection carries no offline asset key (only record-level fields), so
 * the empty `assetKey` makes OfflineImage skip the cache and load the live
 * URL — while still owning the CORS mode per the S3 convention.
 */
export function PreconPhotoLightbox({
  open,
  onClose,
}: {
  open: LightboxPhoto | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={open !== null} onOpenChange={(next) => !next && onClose()}>
      {/* Raise BOTH content and overlay above the z-[60] preview Sheet so the
          lightbox paints on top AND its backdrop reliably dims the Sheet (the
          repo convention for a dialog over another stacking layer — see
          create-tag-surface / pdf-page-picker). z-[70] clears the Sheet and
          stays under the z-[80] filter screens. Harmless in the Sections-step
          use, where nothing competes. */}
      <DialogContent className="z-[70] sm:max-w-3xl" overlayClassName="z-[70]">
        <DialogTitle className="sr-only">
          {open ? `Photo ${open.label}` : "Photo"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Full-size view of the selected report photo. Close to continue
          editing.
        </DialogDescription>
        {open && (
          <OfflineImage
            assetKey=""
            src={open.photo.image ?? open.photo.thumbnail ?? ""}
            alt={`Photo ${open.label}`}
            className="max-h-[75dvh] w-full rounded-md object-contain"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
